import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { saveAs } from 'file-saver'
import { toast } from 'sonner'
import Utils from '../../../utilities/Utils'
import TimeHelper from '../../../helpers/TimeHelper'
import { TransactionsController } from '../../../controllers/accountant'
import { TransactionExportType } from '../../../controllers/accountant/TransactionsController'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group'

interface IProps {
  workspaceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ExportTransactionsModal = (props: IProps) => {
  const { workspaceId, open, onOpenChange } = props
  const { t } = useTranslation()

  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear())
  const [selectedQuarter, setSelectedQuarter] = React.useState(Utils.getCurrentQuarter())
  const [exportType, setExportType] = React.useState<TransactionExportType>('xlsx')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const yearOptions = TimeHelper.getYearRange((new Date()).getFullYear() - 2)
  const yearItems = yearOptions.map(year => ({ label: `${year}`, value: `${year}` }))
  const exportTypeItems = [
    { label: 'Excel', value: 'xlsx' },
    { label: 'CSV', value: 'csv' },
  ]

  const submitExport = async () => {
    setIsSubmitting(true)

    try {
      const { blob, filename } = await TransactionsController.export(workspaceId, exportType, selectedYear, selectedQuarter)
      saveAs(blob, filename)
      onOpenChange(false)
    } catch (error) {
      console.error(error)
      toast.error(t('Accountant::Something went wrong, please try again later.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitExport()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Accountant::Export transactions')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onFormSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>{t('Accountant::Format')}</Label>
            <Select items={exportTypeItems} value={exportType} onValueChange={(value: TransactionExportType) => setExportType(value)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {exportTypeItems.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('Accountant::Year')}</Label>
            <Select items={yearItems} value={`${selectedYear}`} onValueChange={(value: string) => setSelectedYear(Number(value))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={`${year}`}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('Accountant::Quarter')}</Label>
            <ToggleGroup
              variant="outline"
              spacing={0}
              value={[`${selectedQuarter}`]}
              onValueChange={(value) => value[0] && setSelectedQuarter(Number(value[0]))}
            >
              {[1, 2, 3, 4].map(quarter => (
                <ToggleGroupItem key={quarter} value={`${quarter}`} className="flex-1">
                  {`Q${quarter}`}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </form>

        <DialogFooter>
          <Button onClick={submitExport} disabled={isSubmitting}>
            {t('Accountant::Export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ExportTransactionsModal
